import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, Check, X, Loader2 } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { Panel } from "@/components/common/Panel";
import { StatusBadge } from "@/components/common/StatusBadge";
import { SeedBadge } from "@/components/common/SeedBadge";
import { EmptyState } from "@/components/common/EmptyState";
import { MOD_STATUS, formatDate } from "@/lib/nav";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

export default function Moderation() {
  const { can } = useAuth();
  const [items, setItems] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const writable = can("moderation", "write");

  const load = useCallback(() => {
    api.get("/moderation").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const decide = async (id, action) => {
    setBusyId(id);
    try {
      await api.post(`/moderation/${id}/${action}`, { comment: "" });
      toast.success(action === "approve" ? "Freigegeben" : "Abgelehnt");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusyId(null); }
  };

  const pending = (items || []).filter((m) => m.status === "pending");
  const decided = (items || []).filter((m) => m.status !== "pending");

  return (
    <div data-testid="moderation-page">
      <PageHeader title="Moderation" subtitle="Gemeldete Inhalte und Qualitätsfälle prüfen." />

      {items === null ? (
        <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-fundio-gold" /></div>
      ) : (
        <>
          <h2 className="mb-3 flex items-center gap-2 font-display text-base font-semibold text-fundio-ink">
            Warteschlange <span className="rounded-full bg-status-warning-bg px-2 py-0.5 font-mono text-xs text-status-warning">{pending.length}</span>
          </h2>
          {pending.length === 0 ? (
            <EmptyState icon={ShieldCheck} title="Keine offenen Fälle" description="Alle Einreichungen wurden bearbeitet." testid="moderation-empty" />
          ) : (
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
              {pending.map((m) => (
                <div key={m.id} data-testid={`moderation-card-${m.id}`} className="card-surface rounded-xl p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate font-medium text-fundio-ink">{m.title}<SeedBadge show={m.is_seed} /></p>
                      <p className="mt-0.5 text-xs text-fundio-faint">{m.brocki_name} · {m.ref_type} · {formatDate(m.created_at)}</p>
                      <p className="mt-1 text-xs text-fundio-muted">{m.reason}</p>
                    </div>
                    <span className="shrink-0 rounded-md border border-fundio-border px-2 py-1 font-mono text-xs text-fundio-muted">AI {(Number(m.ai_score) * 100).toFixed(0)}%</span>
                  </div>
                  {writable && (
                    <div className="mt-4 flex gap-2">
                      <Button size="sm" data-testid={`moderation-action-approve-${m.id}`} disabled={busyId === m.id} onClick={() => decide(m.id, "approve")} className="flex-1 bg-status-success-bg text-status-success hover:bg-status-success-border"><Check className="h-4 w-4" /> Freigeben</Button>
                      <Button size="sm" variant="ghost" data-testid={`moderation-action-reject-${m.id}`} disabled={busyId === m.id} onClick={() => decide(m.id, "reject")} className="flex-1 border border-status-danger-border text-status-danger hover:bg-status-danger-bg"><X className="h-4 w-4" /> Ablehnen</Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {decided.length > 0 && (
            <div className="mt-8">
              <Panel title="Bearbeitet" testid="moderation-decided-panel" noPad>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-fundio-border bg-fundio-subtle text-left text-xs font-semibold uppercase tracking-wider text-fundio-muted">
                      <th className="px-5 py-3">Titel</th><th className="px-5 py-3">Brocki</th><th className="px-5 py-3">Entscheidung</th><th className="px-5 py-3">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-fundio-border">
                    {decided.map((m) => (
                      <tr key={m.id} className="bg-white" data-testid={`moderation-decided-${m.id}`}>
                        <td className="px-5 py-3.5 font-medium text-fundio-ink">{m.title}<SeedBadge show={m.is_seed} /></td>
                        <td className="px-5 py-3.5 text-fundio-muted">{m.brocki_name}</td>
                        <td className="px-5 py-3.5 text-fundio-faint">{m.decided_by || "—"}</td>
                        <td className="px-5 py-3.5"><StatusBadge map={MOD_STATUS} value={m.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Panel>
            </div>
          )}
        </>
      )}
    </div>
  );
}
